import React from 'react';
import PropTypes from 'prop-types';
import { chromeCornerClass } from '@/slideshow/chrome';
import { VIEWER_CONTROL_BACKDROP, VIEWER_CONTROL_STYLE } from '@/theme/viewer-look';

/**
 * Image counter showing current position in gallery
 * Supports single image (e.g., "3 / 13") or spread range (e.g., "1 - 2 / 13")
 * Can also show a hint text instead of counter (e.g., "swipe left →")
 * When inline, flows in layout (e.g. in a flex bar) instead of absolute positioning.
 *
 * @param {number} current - Current position (1-indexed)
 * @param {number} currentEnd - End of range for spreads (optional)
 * @param {number} total - Total number of images
 * @param {string} hint - Optional hint text to show instead of counter
 * @param {boolean} inline - If true, use relative positioning for use in flex/flow layouts
 * @param {string} corner - Screen corner (tl, tr, bl, br) or hidden; ignored when inline
 */
const ImageCounter = ({ current, currentEnd, total, hint, inline = false, corner = 'br' }) => {
    if (corner === 'hidden') return null;
    return (
        <div
            data-slideshow-counter=""
            className={inline
                ? 'text-[10px] md:text-[13px] tabular-nums p-[2px] whitespace-nowrap'
                : `absolute px-1 ${chromeCornerClass('counter', corner)} text-[10px] md:text-[13px] tabular-nums p-[2px] whitespace-nowrap`
            }
            style={{ ...VIEWER_CONTROL_STYLE, ...VIEWER_CONTROL_BACKDROP, zIndex: 20 }}
        >
            {hint ? hint : (
                currentEnd && currentEnd !== current
                    ? `${current} - ${currentEnd} / ${total}`
                    : `${current} / ${total}`
            )}
        </div>
    );
};

ImageCounter.propTypes = {
    current: PropTypes.number,
    currentEnd: PropTypes.number,
    total: PropTypes.number,
    hint: PropTypes.string,
    inline: PropTypes.bool,
    corner: PropTypes.oneOf(['tl', 'tr', 'bl', 'br', 'hidden']),
};

export default ImageCounter;
